<template>
  <div class="pb-20">
    <Head :title="entryType === 'shopping' ? 'Nueva Compra' : 'Nuevo Gasto'" />

    <!-- Header con botón volver y título dinámico -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
      <div class="flex items-center gap-4">
        <button 
          class="p-2.5 text-teal-700 bg-white border border-teal-200 rounded-xl hover:bg-teal-50 transition-colors shadow-sm"
          title="Volver atrás"
          @click="back()"
        >
          <i class="fa fa-arrow-left" />
        </button>
        <div>
          <h1 class="text-3xl font-extrabold text-gray-900 tracking-tight">
            {{ entryType === 'shopping' ? 'Nueva Compra de Mercancía' : 'Nuevo Gasto Operativo' }}
          </h1>
          <p class="text-xs text-gray-500 mt-0.5">
            {{ entryType === 'shopping' ? 'Registre compras a proveedores (suman al inventario)' : 'Registre salidas y egresos directos del negocio (sin afectar existencias)' }}
          </p>
        </div>
      </div>

      <!-- Control de Moneda y Tasa de Cambio (visible en Compra) -->
      <div v-show="entryType === 'shopping'" class="flex flex-wrap items-center gap-3 bg-white p-2 rounded-2xl border border-gray-100 shadow-sm">
        <!-- Switch de Moneda -->
        <div class="flex items-center bg-gray-100 p-1 rounded-xl">
          <button 
            type="button"
            :class="currency === 'USD' ? 'bg-teal-600 text-white shadow-sm font-bold' : 'text-gray-600 hover:text-gray-900 font-medium'"
            class="px-3.5 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition-all"
            @click="setCurrency('USD')"
          >
            <i class="fa fa-dollar-sign text-xs" />
            <span>Dólares ($)</span>
          </button>
          <button 
            type="button"
            :class="currency === 'VES' ? 'bg-teal-600 text-white shadow-sm font-bold' : 'text-gray-600 hover:text-gray-900 font-medium'"
            class="px-3.5 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition-all"
            @click="setCurrency('VES')"
          >
            <span class="font-extrabold text-xs">Bs.</span>
            <span>Bolívares</span>
          </button>
        </div>

        <!-- Tasa de Cambio Editable -->
        <div class="flex items-center gap-2 pl-2 border-l border-gray-200 text-xs">
          <span class="text-gray-500 font-medium flex items-center gap-1">
            <i class="fa fa-exchange-alt text-teal-600 text-[10px]" />
            Tasa: 1 $ =
          </span>
          <div class="relative">
            <input 
              v-model.number="exchange_rate"
              type="number"
              step="0.01"
              min="0"
              class="w-24 px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-lg text-xs font-bold text-gray-800 text-right focus:bg-white focus:ring-teal-500 focus:border-teal-500 transition-colors"
              placeholder="0.00"
              @input="onExchangeRateChange"
            />
          </div>
          <span class="text-gray-600 font-bold">Bs.</span>
        </div>
      </div>
    </div>

    <!-- Selector de Tipo de Operación: Compra vs Gasto -->
    <div class="mb-8 bg-white p-2 rounded-2xl border border-gray-100 shadow-sm grid grid-cols-1 sm:grid-cols-2 gap-2">
      <button 
        type="button" 
        :class="entryType === 'shopping' ? 'bg-teal-600 text-white shadow-md font-bold ring-2 ring-teal-500/20' : 'text-gray-600 hover:bg-gray-50 font-semibold'"
        class="py-3 px-4 rounded-xl text-sm flex items-center gap-3 transition-all text-left"
        @click="entryType = 'shopping'"
      >
        <div class="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" :class="entryType === 'shopping' ? 'bg-white/20 text-white' : 'bg-teal-50 text-teal-700'">
          <i class="fa fa-boxes text-lg" />
        </div>
        <div>
          <div class="leading-tight text-base font-bold">Compra de Mercancía</div>
          <div class="text-[11px] opacity-80 mt-0.5 font-normal">+ Suma al inventario &bull; Requiere proveedor &bull; Costos y precios</div>
        </div>
      </button>

      <button 
        type="button" 
        :class="entryType === 'spent' ? 'bg-amber-500 text-white shadow-md font-bold ring-2 ring-amber-500/20' : 'text-gray-600 hover:bg-gray-50 font-semibold'"
        class="py-3 px-4 rounded-xl text-sm flex items-center gap-3 transition-all text-left"
        @click="entryType = 'spent'"
      >
        <div class="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" :class="entryType === 'spent' ? 'bg-white/20 text-white' : 'bg-amber-50 text-amber-700'">
          <i class="fa fa-receipt text-lg" />
        </div>
        <div>
          <div class="leading-tight text-base font-bold">Gasto Operativo</div>
          <div class="text-[11px] opacity-80 mt-0.5 font-normal">Solo egreso al negocio &bull; Sin inventario &bull; Proveedor opcional</div>
        </div>
      </button>
    </div>

    <!-- Sección de Compra (Existente) -->
    <div v-show="entryType === 'shopping'" class="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <!-- Left Column: Info & Products -->
      <div class="lg:col-span-2 space-y-8">
        <!-- Información de la compra -->
        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div class="p-6 border-b border-gray-50 bg-gray-50/50">
            <h3 class="text-xl font-bold text-gray-800 flex items-center gap-2">
              <i class="fa fa-info-circle text-teal-600" />
              Información de la Compra
            </h3>
          </div>
          <div class="p-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Selector de Proveedor con botón de creación directa -->
              <div class="space-y-1">
                <div class="flex justify-between items-center mb-1">
                  <label class="block text-sm font-bold text-gray-700">Proveedor *</label>
                  <button 
                    type="button" 
                    class="text-xs text-teal-600 hover:text-teal-800 font-semibold flex items-center gap-1 transition-colors"
                    @click="openQuickProviderModal"
                  >
                    <i class="fa fa-plus-circle" />
                    <span>Crear Proveedor</span>
                  </button>
                </div>
                <div class="flex gap-2">
                  <select 
                    v-model="form.provider_id"
                    class="w-full rounded-lg border-gray-300 focus:ring-teal-500 focus:border-teal-500 transition-shadow text-sm"
                    :class="{'border-red-500': form.errors.provider_id}"
                  >
                    <option value="">Seleccionar proveedor</option>
                    <option 
                      v-for="provider in providersList" 
                      :key="provider.id" 
                      :value="provider.id"
                    >
                      {{ provider.name }} {{ provider.company_name && provider.company_name !== provider.name ? '- ' + provider.company_name : '' }} {{ provider.tax_id ? '(' + provider.tax_id + ')' : '' }}
                    </option>
                  </select>
                  <button 
                    type="button" 
                    class="px-3 py-2 bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 rounded-lg text-sm flex items-center justify-center transition-colors shadow-sm flex-shrink-0"
                    title="Crear nuevo proveedor aquí mismo"
                    @click="openQuickProviderModal"
                  >
                    <i class="fa fa-user-plus" />
                  </button>
                </div>
                <div v-if="form.errors.provider_id" class="text-red-500 text-xs mt-1">{{ form.errors.provider_id }}</div>
              </div>

              <!-- Número de Factura -->
              <text-input
                v-model="form.invoice_number"
                :error="form.errors.invoice_number"
                label="Número de Factura *"
                placeholder="Ingrese el número de factura..."
                input-class="focus:ring-teal-500 focus:border-teal-500"
              />

              <!-- Tipo de Pago -->
              <div class="space-y-1">
                <label class="block text-sm font-bold text-gray-700">Tipo de Pago *</label>
                <select 
                  v-model="form.payment_type"
                  class="w-full rounded-lg border-gray-300 focus:ring-teal-500 focus:border-teal-500 transition-shadow"
                >
                  <option value="cash">Contado</option>
                  <option value="credit">Crédito</option>
                </select>
              </div>

              <!-- Fecha del documento -->
              <text-input
                v-model="form.date"
                :error="form.errors.date"
                type="date"
                label="Fecha del Documento"
                class="w-full"
                input-class="focus:ring-teal-500 focus:border-teal-500"
              />

              <!-- Fecha de Vencimiento (solo para crédito) -->
              <text-input
                v-if="form.payment_type === 'credit'"
                v-model="form.due_date"
                :error="form.errors.due_date"
                type="date"
                label="Fecha de Vencimiento *"
                class="w-full"
                input-class="focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>
        </div>

        <!-- Búsqueda de Productos con Crear Producto rápido -->
        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div class="p-6 border-b border-gray-50 bg-gray-50/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h3 class="text-xl font-bold text-gray-800 flex items-center gap-2">
                <i class="fa fa-search text-teal-600" />
                Búsqueda de Productos
              </h3>
              <p class="text-xs text-gray-400 mt-0.5">Seleccione los productos existentes o cree uno nuevo rápidamente</p>
            </div>

            <button 
              type="button"
              class="px-3.5 py-2 bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 rounded-xl flex items-center gap-2 transition-colors font-semibold text-sm shadow-sm" 
              @click="openQuickProductModal"
            >
              <i class="fa fa-plus-circle text-teal-600" />
              <span>Crear producto</span>
            </button>
          </div>

          <div class="p-6">
            <div class="relative mb-6">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i class="fa fa-search text-gray-400" />
              </div>
              <input 
                v-model="form.search.terms" 
                type="text" 
                class="block w-full pl-10 pr-3 py-3 bg-white border border-gray-200 rounded-xl focus:ring-teal-500 focus:border-teal-500 placeholder-gray-400 transition-colors shadow-sm" 
                placeholder="Buscar por nombre, código o descripción..."
              />
            </div>

            <div class="overflow-x-auto border border-gray-100 rounded-xl">
              <table class="w-full whitespace-nowrap">
                <thead>
                  <tr class="text-left bg-gray-50">
                    <th class="py-3 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Código</th>
                    <th class="py-3 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Producto</th>
                    <th class="py-3 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">
                      Costo ({{ currency === 'VES' ? 'Bs.' : '$' }})
                    </th>
                    <th class="py-3 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">
                      Precio Venta ({{ currency === 'VES' ? 'Bs.' : '$' }})
                    </th>
                    <th class="py-3 px-4 w-12" />
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                  <tr 
                    v-for="product in products" 
                    :key="product.id"
                    class="hover:bg-teal-50/50 cursor-pointer transition-colors group"
                    @click="selectProduct(product)"
                  >
                    <td class="py-3 px-6 font-mono text-sm text-gray-500">{{ product?.code }}</td>
                    <td class="py-3 px-6 font-medium text-gray-900">{{ product?.name }}</td>
                    <td class="py-3 px-6 text-right">
                      <div class="text-teal-700 font-bold">
                        {{ currency === 'VES' ? formatBs(getCostInBs(product?.cost)) : formatUSD(product?.cost) }}
                      </div>
                      <div class="text-[11px] text-gray-400 font-mono">
                        {{ currency === 'VES' ? formatUSD(product?.cost) : formatBs(getCostInBs(product?.cost)) }}
                      </div>
                    </td>
                    <td class="py-3 px-6 text-right">
                      <div class="font-bold text-gray-900">
                        {{ currency === 'VES' ? formatBs(getCostInBs(sellingPrices(product))) : formatUSD(sellingPrices(product)) }}
                      </div>
                      <div class="text-[11px] text-gray-400 font-mono">
                        {{ currency === 'VES' ? formatUSD(sellingPrices(product)) : formatBs(getCostInBs(sellingPrices(product))) }}
                      </div>
                    </td>
                    <td class="py-3 px-4 text-center">
                      <span class="text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity">
                        <i class="fa fa-plus-circle text-lg" />
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!products?.length">
                    <td colspan="5" class="py-8 text-center text-gray-400">
                      <div class="flex flex-col items-center justify-center">
                        <i class="fa fa-box-open text-3xl mb-2 text-gray-300" />
                        <p class="mb-2">No se encontraron productos</p>
                        <button 
                          type="button"
                          class="px-3.5 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-sm"
                          @click="openQuickProductModal"
                        >
                          <i class="fa fa-plus" />
                          <span>Crear producto nuevo</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Carrito de Compras -->
        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div class="p-6 border-b border-gray-50 bg-gray-50/50 flex justify-between items-center">
            <h3 class="text-xl font-bold text-gray-800 flex items-center gap-2">
              <i class="fa fa-shopping-cart text-teal-600" />
              Productos a Comprar
            </h3>
            <div class="flex items-center gap-2">
              <span class="px-2.5 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-xs font-bold">
                Moneda: {{ currency === 'VES' ? 'Bs. Bolívares' : '$ USD' }}
              </span>
              <span class="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-bold">
                {{ form.products.length }} Items
              </span>
            </div>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="bg-gray-50 text-left border-b border-gray-100">
                  <th class="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Producto</th>
                  <th class="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Cantidad</th>
                  <th class="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">
                    Costo Unitario ({{ currency === 'VES' ? 'Bs.' : '$' }})
                  </th>
                  <th class="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">
                    Total ({{ currency === 'VES' ? 'Bs.' : '$' }})
                  </th>
                  <th class="p-4" />
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="(product, index) in form.products" :key="product.id" class="hover:bg-gray-50 transition-colors">
                  <td class="p-4">
                    <div class="font-medium text-gray-900">{{ product.name }}</div>
                    <div class="text-xs text-gray-500 font-mono">{{ product.code }}</div>
                  </td>
                  <td class="p-4">
                    <div class="flex justify-center">
                      <input 
                        v-model.number="form.products[index].quantity"
                        type="number"
                        min="1"
                        class="w-20 text-center rounded-lg border-gray-200 focus:ring-teal-500 focus:border-teal-500 py-1 text-sm shadow-sm"
                      />
                    </div>
                  </td>
                  <td class="p-4">
                    <div class="flex flex-col items-end">
                      <!-- Input en USD si moneda activa es USD -->
                      <div v-if="currency === 'USD'" class="relative">
                        <span class="absolute left-2.5 top-1.5 text-gray-400 text-xs font-bold">$</span>
                        <input 
                          v-model.number="form.products[index].cost"
                          type="number"
                          step="0.01"
                          min="0"
                          class="w-28 pl-6 pr-2 text-right rounded-lg border-gray-200 focus:ring-teal-500 focus:border-teal-500 py-1 text-sm shadow-sm"
                          @input="updateCostFromUSD(form.products[index])"
                        />
                      </div>
                      <!-- Input en Bs si moneda activa es VES -->
                      <div v-else class="relative">
                        <span class="absolute left-2 top-1.5 text-gray-400 text-[10px] font-bold">Bs.</span>
                        <input 
                          v-model.number="form.products[index].cost_bs"
                          type="number"
                          step="0.01"
                          min="0"
                          class="w-32 pl-7 pr-2 text-right rounded-lg border-gray-200 focus:ring-teal-500 focus:border-teal-500 py-1 text-sm shadow-sm"
                          @input="updateCostFromBs(form.products[index])"
                        />
                      </div>
                      <!-- Subtexto con valor convertido -->
                      <div class="text-[11px] text-gray-400 mt-1 font-mono">
                        <span v-if="currency === 'USD'">≈ {{ formatBs(getCostBs(form.products[index])) }}</span>
                        <span v-else>≈ {{ formatUSD(form.products[index].cost) }}</span>
                      </div>
                    </div>
                  </td>
                  <td class="p-4 text-right">
                    <div class="font-bold text-gray-900 text-base">
                      <span v-if="currency === 'USD'">{{ formatUSD(form.products[index].cost * form.products[index].quantity) }}</span>
                      <span v-else>{{ formatBs(getCostBs(form.products[index]) * form.products[index].quantity) }}</span>
                    </div>
                    <div class="text-xs text-gray-400 font-mono">
                      <span v-if="currency === 'USD'">≈ {{ formatBs(getCostBs(form.products[index]) * form.products[index].quantity) }}</span>
                      <span v-else>≈ {{ formatUSD(form.products[index].cost * form.products[index].quantity) }}</span>
                    </div>
                  </td>
                  <td class="p-4 text-center">
                    <button
                      class="w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-full transition-colors"
                      title="Eliminar producto"
                      @click="removeProduct(product)"
                    >
                      <i class="fa fa-trash" />
                    </button>
                  </td>
                </tr>
                <tr v-if="!form.products.length">
                  <td colspan="5" class="py-12 text-center">
                    <div class="flex flex-col items-center justify-center text-gray-300">
                      <i class="fa fa-shopping-basket text-4xl mb-2" />
                      <p>El carrito está vacío</p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Right Column: Summary & Payment -->
      <div class="space-y-8">
        <!-- Totals & Payment -->
        <div class="bg-white rounded-2xl shadow-xl border border-teal-100 overflow-hidden sticky top-8">
          <div class="p-6 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
            <h3 class="text-xl font-bold flex items-center gap-2">
              <i class="fa fa-receipt" />
              Resumen de Compra
            </h3>
          </div>
          
          <div class="p-6 space-y-4">
            <!-- Destino -->
            <div class="space-y-1">
              <label class="block text-sm font-bold text-gray-700">Depósito Destino *</label>
              <select 
                v-model="form.deposit_id"
                class="w-full rounded-xl border-gray-200 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">Seleccione depósito</option>
                <option v-for="deposit in deposits" :key="deposit.id" :value="deposit.id">
                  {{ deposit.name }}
                </option>
              </select>
            </div>

            <!-- Subtotal -->
            <div class="flex justify-between items-center text-gray-600">
              <span class="font-medium">Subtotal:</span>
              <div class="text-right">
                <div class="font-bold text-gray-900">
                  {{ currency === 'VES' ? formatBs(getSubtotalBs()) : formatUSD(getSubtotal()) }}
                </div>
                <div class="text-xs text-gray-400 font-mono">
                  ≈ {{ currency === 'VES' ? formatUSD(getSubtotal()) : formatBs(getSubtotalBs()) }}
                </div>
              </div>
            </div>

            <!-- Descuento -->
            <div class="space-y-3 pt-2 border-t border-gray-50">
              <div class="flex justify-between items-center text-red-600">
                <span class="font-medium">Descuento:</span>
                <div class="text-right">
                  <div class="font-bold">
                    - {{ currency === 'VES' ? formatBs(getDiscountBs()) : formatUSD(form.discount_amount || 0) }}
                  </div>
                  <div v-if="parseFloat(form.discount_amount) > 0" class="text-xs text-red-400 font-mono">
                    ≈ - {{ currency === 'VES' ? formatUSD(form.discount_amount) : formatBs(getDiscountBs()) }}
                  </div>
                </div>
              </div>
              <div class="grid grid-cols-2 gap-2">
                <div class="relative">
                  <input 
                    v-model="form.discount_percentage"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    class="w-full pl-3 pr-8 py-2 text-sm rounded-lg border-gray-200 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="%"
                    @input="calculateDiscount"
                  />
                  <span class="absolute right-3 top-2 text-gray-400 text-xs">%</span>
                </div>
                <div class="relative">
                  <span class="absolute left-2.5 top-2 text-gray-400 text-xs font-bold">
                    {{ currency === 'VES' ? 'Bs' : '$' }}
                  </span>
                  <input 
                    v-if="currency === 'USD'"
                    v-model="form.discount_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    class="w-full pl-6 pr-3 py-2 text-sm rounded-lg border-gray-200 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Monto"
                    @input="calculateDiscountPercentage"
                  />
                  <input 
                    v-else
                    v-model="discount_amount_bs"
                    type="number"
                    step="0.01"
                    min="0"
                    class="w-full pl-8 pr-3 py-2 text-sm rounded-lg border-gray-200 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Monto"
                    @input="onDiscountBsChange"
                  />
                </div>
              </div>
            </div>

            <!-- Total -->
            <div class="pt-4 border-t border-teal-50">
              <div class="bg-gradient-to-br from-teal-50 to-teal-100/60 p-4 rounded-2xl border border-teal-200/60">
                <div class="flex justify-between items-center mb-1">
                  <span class="text-teal-800 font-bold text-base">Total Compra:</span>
                  <span class="text-teal-950 font-black text-2xl">
                    {{ currency === 'VES' ? formatBs(getTotalBs()) : formatUSD(getTotal()) }}
                  </span>
                </div>
                <div class="text-right text-xs text-teal-700 font-medium font-mono">
                  ≈ {{ currency === 'VES' ? formatUSD(getTotal()) : formatBs(getTotalBs()) }}
                </div>
              </div>
            </div>

            <!-- Payment Methods Section -->
            <div class="pt-4 space-y-4">
              <h4 class="font-bold text-gray-800 border-b border-gray-50 pb-2 flex items-center gap-2">
                <i class="fa fa-wallet text-teal-600" />
                Método de Pago
              </h4>
              <shopping-payment-box
                ref="paymentBox"
                :payment_methods="payment_methods"
                :balance="form.total ? parseFloat(form.total) - getTotalPaid() : 0"
                :change="parseFloat(exchange_rate) || 1"
                @add-payment="addPayment"
              />
              
              <!-- Added Payments -->
              <div v-if="form.payments && form.payments.length > 0" class="space-y-3">
                <div 
                  v-for="(payment, index) in form.payments" 
                  :key="index"
                  class="bg-gray-50 p-3 rounded-xl border border-gray-100 flex justify-between items-center group"
                >
                  <div class="text-sm">
                    <div class="font-bold text-gray-800">{{ getPaymentMethodName(payment.method) }}</div>
                    <div class="text-gray-600 font-medium font-mono text-xs">
                      <span v-if="payment.amount_bs > 0">
                        {{ formatBs(payment.amount_bs) }} ({{ formatUSD(payment.amount) }})
                      </span>
                      <span v-else>
                        {{ formatUSD(payment.amount) }} <span class="text-gray-400">≈ {{ formatBs(payment.amount * (exchange_rate || 1)) }}</span>
                      </span>
                    </div>
                  </div>
                  <button 
                    class="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors" 
                    @click="removePayment(index)"
                  >
                    <i class="fa fa-times" />
                  </button>
                </div>
                <div class="flex justify-between items-center px-2 py-1 text-teal-700 font-bold text-sm">
                  <span>Total Pagado:</span>
                  <div class="text-right">
                    <div>{{ currency === 'VES' ? formatBs(getTotalPaidBs()) : formatUSD(getTotalPaid()) }}</div>
                    <div class="text-[11px] text-gray-400 font-normal font-mono">
                      ≈ {{ currency === 'VES' ? formatUSD(getTotalPaid()) : formatBs(getTotalPaidBs()) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Submit Button -->
            <button 
              class="w-full py-4 bg-teal-600 hover:bg-teal-700 text-white rounded-2xl font-bold text-lg shadow-lg flex items-center justify-center gap-3 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="!form.products.length"
              @click="generate"
            >
              <i class="fa fa-check-circle" />
              Generar Compra
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección de Gasto Operativo -->
    <div v-show="entryType === 'spent'" class="max-w-4xl mx-auto">
      <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <!-- Card Header -->
        <div class="p-6 bg-gradient-to-r from-amber-500 to-amber-600 text-white flex justify-between items-center">
          <div>
            <h2 class="text-xl font-extrabold flex items-center gap-2.5">
              <i class="fa fa-receipt" />
              <span>Registrar Gasto Operativo</span>
            </h2>
            <p class="text-xs text-amber-100 mt-1">
              Egreso directo al negocio. No suma existencias al inventario.
            </p>
          </div>
          <span class="px-3 py-1 bg-white/20 rounded-full text-xs font-bold uppercase tracking-wider">
            Egreso Financiero
          </span>
        </div>

        <form class="p-6 md:p-8 space-y-6" @submit.prevent="saveSpent">
          <!-- Information Alert: Las 3 reglas -->
          <div class="p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-start gap-3">
            <i class="fa fa-info-circle text-amber-600 text-base mt-0.5" />
            <div class="space-y-1">
              <p class="font-bold text-amber-950">Información sobre este registro:</p>
              <ul class="list-disc list-inside space-y-0.5 text-amber-800">
                <li><strong>Egreso financiero:</strong> Se contabiliza como salida de dinero en las finanzas del negocio.</li>
                <li><strong>Sin impacto en inventario:</strong> No incrementa las existencias de productos ni depósitos.</li>
                <li><strong>Proveedor opcional:</strong> Puede asociarse a un proveedor registrado o dejarse como gasto interno.</li>
              </ul>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Concepto / Nombre -->
            <div class="md:col-span-2">
              <label class="block text-sm font-bold text-gray-700 mb-1">Concepto o Nombre del Gasto *</label>
              <input 
                v-model="spentForm.name"
                type="text"
                required
                placeholder="Ej: Pago de electricidad, Alquiler de local, Honorarios contables..."
                class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-amber-500 focus:border-amber-500 text-sm transition-colors"
              />
            </div>

            <!-- Categoría del Gasto -->
            <div>
              <label class="block text-sm font-bold text-gray-700 mb-1">Categoría del Gasto *</label>
              <select 
                v-model="spentForm.spent_category_id"
                required
                class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-amber-500 focus:border-amber-500 text-sm transition-colors"
              >
                <option value="">Seleccione una categoría</option>
                <option v-for="cat in spentCategoriesList" :key="cat.id" :value="cat.id">
                  {{ cat.name }}
                </option>
              </select>
            </div>

            <!-- Proveedor (Opcional) -->
            <div>
              <div class="flex justify-between items-center mb-1">
                <label class="block text-sm font-bold text-gray-700">Proveedor (Opcional)</label>
                <button 
                  type="button" 
                  class="text-xs text-amber-600 hover:text-amber-800 font-semibold flex items-center gap-1 transition-colors"
                  @click="openQuickProviderModal"
                >
                  <i class="fa fa-plus-circle" />
                  <span>Crear Proveedor</span>
                </button>
              </div>
              <select 
                v-model="spentForm.provider_id"
                class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-amber-500 focus:border-amber-500 text-sm transition-colors"
              >
                <option :value="null">Sin proveedor (Gasto interno)</option>
                <option v-for="provider in providersList" :key="provider.id" :value="provider.id">
                  {{ provider.name }} {{ provider.company_name && provider.company_name !== provider.name ? '- ' + provider.company_name : '' }}
                </option>
              </select>
            </div>

            <!-- Moneda y Monto -->
            <div>
              <label class="block text-sm font-bold text-gray-700 mb-1">Moneda del Gasto *</label>
              <div class="flex bg-gray-100 p-1 rounded-xl">
                <button 
                  type="button" 
                  :class="spentForm.currency === 'USD' ? 'bg-white text-amber-700 shadow-sm font-bold' : 'text-gray-500'"
                  class="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
                  @click="setSpentCurrency('USD')"
                >
                  Dólares ($)
                </button>
                <button 
                  type="button" 
                  :class="spentForm.currency === 'BS' ? 'bg-white text-amber-700 shadow-sm font-bold' : 'text-gray-500'"
                  class="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
                  @click="setSpentCurrency('BS')"
                >
                  Bolívares (Bs.)
                </button>
              </div>
            </div>

            <!-- Monto en Bs (si aplica) -->
            <div v-if="spentForm.currency === 'BS'">
              <label class="block text-sm font-bold text-gray-700 mb-1">Monto en Bolívares (Bs.) *</label>
              <input 
                v-model.number="spentBsAmount"
                type="number"
                step="0.01"
                min="0.01"
                required
                placeholder="0.00"
                class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-amber-500 focus:border-amber-500 text-sm font-bold text-gray-900 transition-colors"
                @input="calculateSpentAmountFromBs"
              />
              <p class="text-xs text-gray-500 mt-1">
                Tasa de cambio: {{ spentForm.exchange_rate || exchange_rate }} Bs. / USD
              </p>
            </div>

            <!-- Monto en USD -->
            <div :class="spentForm.currency === 'BS' ? 'md:col-span-2' : ''">
              <label class="block text-sm font-bold text-gray-700 mb-1">
                {{ spentForm.currency === 'BS' ? 'Monto equivalente en USD (Calculado autom.)' : 'Monto en USD *' }}
              </label>
              <input 
                v-model.number="spentForm.amount"
                type="number"
                step="0.01"
                min="0.01"
                required
                :readonly="spentForm.currency === 'BS'"
                placeholder="0.00"
                class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-amber-500 focus:border-amber-500 text-sm font-bold text-gray-900 transition-colors"
              />
            </div>

            <!-- Fecha del Documento -->
            <div>
              <label class="block text-sm font-bold text-gray-700 mb-1">Fecha del Gasto *</label>
              <input 
                v-model="spentForm.date"
                type="date"
                required
                class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-amber-500 focus:border-amber-500 text-sm transition-colors"
              />
            </div>

            <!-- Método de Pago -->
            <div>
              <label class="block text-sm font-bold text-gray-700 mb-1">Método de Pago *</label>
              <select 
                v-model="spentForm.method"
                required
                class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-amber-500 focus:border-amber-500 text-sm transition-colors"
              >
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">Transferencia Bancaria</option>
                <option value="pago-movil">Pago Móvil</option>
                <option value="zelle">Zelle</option>
                <option value="punto-de-venta">Punto de Venta</option>
              </select>
            </div>

            <!-- Descripción o Referencia -->
            <div class="md:col-span-2">
              <label class="block text-sm font-bold text-gray-700 mb-1">Descripción o Notas adicionales</label>
              <textarea 
                v-model="spentForm.description"
                rows="3"
                placeholder="Número de referencia, detalles de la factura o comprobante, observaciones..."
                class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-amber-500 focus:border-amber-500 text-sm transition-colors"
              />
            </div>
          </div>

          <!-- Botones de Acción -->
          <div class="pt-6 border-t border-gray-100 flex items-center justify-end gap-4">
            <Link 
              href="/shoppings?tab=spents"
              class="px-5 py-2.5 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors"
            >
              Cancelar
            </Link>

            <button 
              type="submit" 
              :disabled="isSavingSpent"
              class="px-8 py-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-xl font-bold text-sm shadow-md shadow-amber-500/20 transition-all flex items-center gap-2"
            >
              <i v-if="isSavingSpent" class="fa fa-spinner fa-spin" />
              <i v-else class="fa fa-check-circle" />
              <span>Guardar Gasto</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Crear Proveedor -->
    <div v-if="showQuickProviderModal" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4">
      <div class="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 border border-gray-100 max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-5 pb-3 border-b border-gray-100">
          <h3 class="text-lg font-bold text-gray-900 flex items-center gap-2">
            <i class="fa fa-user-plus text-teal-600" />
            Crear Proveedor
          </h3>
          <button class="text-gray-400 hover:text-gray-600 transition-colors" @click="showQuickProviderModal = false">
            <i class="fa fa-times" />
          </button>
        </div>

        <form class="space-y-4" @submit.prevent="saveQuickProvider">
          <!-- Nombre / Contacto -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">Nombre del Proveedor / Contacto <span class="text-red-500">*</span></label>
            <input 
              v-model="quickProviderForm.name" 
              type="text" 
              required 
              placeholder="Ej: Distribuidora Los Andes C.A."
              class="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              :class="providerErrors.name ? 'border-red-500' : 'border-gray-200'"
            />
            <p v-if="providerErrors.name" class="text-xs text-red-500 mt-1">{{ providerErrors.name[0] }}</p>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Empresa / Razón Social -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">Razón Social</label>
              <input 
                v-model="quickProviderForm.company_name" 
                type="text" 
                placeholder="Nombre comercial o razón social"
                class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>

            <!-- RIF / Tax ID -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">RIF / C.I. / Tax ID</label>
              <input 
                v-model="quickProviderForm.tax_id" 
                type="text" 
                placeholder="Ej: J-12345678-9"
                class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Teléfono -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">Teléfono</label>
              <input 
                v-model="quickProviderForm.phone" 
                type="text" 
                placeholder="Ej: 0414-1234567"
                class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>

            <!-- Email -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">Correo Electrónico</label>
              <input 
                v-model="quickProviderForm.email" 
                type="email" 
                placeholder="correo@proveedor.com"
                class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>
          </div>

          <!-- Dirección -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">Dirección</label>
            <input 
              v-model="quickProviderForm.address" 
              type="text" 
              placeholder="Dirección fiscal o de almacén"
              class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>

          <div class="flex justify-end items-center gap-3 pt-4 border-t border-gray-100">
            <button 
              type="button" 
              class="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
              @click="showQuickProviderModal = false"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              :disabled="isSavingProvider"
              class="px-5 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm"
            >
              <i v-if="isSavingProvider" class="fa fa-spinner fa-spin" />
              <i v-else class="fa fa-check" />
              <span>Guardar Proveedor</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Crear Producto (como en Inventories/Create) -->
    <div v-if="showQuickProductModal" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4">
      <div class="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 border border-gray-100 max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-5 pb-3 border-b border-gray-100">
          <h3 class="text-lg font-bold text-gray-900 flex items-center gap-2">
            <i class="fa fa-box-open text-teal-600" />
            Crear producto
          </h3>
          <button class="text-gray-400 hover:text-gray-600 transition-colors" @click="showQuickProductModal = false">
            <i class="fa fa-times" />
          </button>
        </div>

        <form class="space-y-4" @submit.prevent="saveQuickProduct">
          <!-- Nombre -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">Nombre del Producto <span class="text-red-500">*</span></label>
            <input 
              v-model="quickProductForm.name" 
              type="text" 
              required 
              placeholder="Ej: Filtro de Aceite PH-8A"
              class="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              :class="productErrors.name ? 'border-red-500' : 'border-gray-200'"
            />
            <p v-if="productErrors.name" class="text-xs text-red-500 mt-1">{{ productErrors.name[0] }}</p>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Código -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">Código / Referencia</label>
              <input 
                v-model="quickProductForm.code" 
                type="text" 
                placeholder="Autogenerado si está vacío"
                class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>

            <!-- Categoría -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">Categoría</label>
              <select 
                v-model="quickProductForm.product_category_id"
                class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-white"
              >
                <option value="">Sin categoría</option>
                <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
              </select>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Costo -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Costo Unitario ({{ currency === 'VES' ? 'Bs.' : '$' }})
              </label>
              <div class="relative">
                <span class="absolute left-2.5 top-2 text-gray-400 text-xs font-bold">
                  {{ currency === 'VES' ? 'Bs' : '$' }}
                </span>
                <input 
                  v-if="currency === 'USD'"
                  v-model.number="quickProductForm.cost" 
                  type="number" 
                  step="0.01" 
                  min="0"
                  placeholder="0.00"
                  class="w-full pl-6 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                />
                <input 
                  v-else
                  v-model.number="quickProductForm.cost_bs" 
                  type="number" 
                  step="0.01" 
                  min="0"
                  placeholder="0.00"
                  class="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                />
              </div>
              <p v-if="exchange_rate > 0" class="text-[11px] text-gray-400 mt-1 font-mono">
                <span v-if="currency === 'USD'">≈ Bs. {{ ((quickProductForm.cost || 0) * exchange_rate).toFixed(2) }}</span>
                <span v-else>≈ $ {{ (quickProductForm.cost_bs ? (quickProductForm.cost_bs / exchange_rate).toFixed(2) : '0.00') }}</span>
              </p>
            </div>

            <!-- Precio de Venta -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Precio Venta ({{ currency === 'VES' ? 'Bs.' : '$' }}) <span class="text-red-500">*</span>
              </label>
              <div class="relative">
                <span class="absolute left-2.5 top-2 text-gray-400 text-xs font-bold">
                  {{ currency === 'VES' ? 'Bs' : '$' }}
                </span>
                <input 
                  v-if="currency === 'USD'"
                  v-model.number="quickProductForm.price" 
                  type="number" 
                  step="0.01" 
                  min="0"
                  required
                  placeholder="0.00"
                  class="w-full pl-6 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  :class="productErrors.price ? 'border-red-500' : 'border-gray-200'"
                />
                <input 
                  v-else
                  v-model.number="quickProductForm.price_bs" 
                  type="number" 
                  step="0.01" 
                  min="0"
                  required
                  placeholder="0.00"
                  class="w-full pl-8 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  :class="productErrors.price ? 'border-red-500' : 'border-gray-200'"
                />
              </div>
              <p v-if="exchange_rate > 0" class="text-[11px] text-gray-400 mt-1 font-mono">
                <span v-if="currency === 'USD'">≈ Bs. {{ ((quickProductForm.price || 0) * exchange_rate).toFixed(2) }}</span>
                <span v-else>≈ $ {{ (quickProductForm.price_bs ? (quickProductForm.price_bs / exchange_rate).toFixed(2) : '0.00') }}</span>
              </p>
              <p v-if="productErrors.price" class="text-xs text-red-500 mt-1">{{ productErrors.price[0] }}</p>
            </div>
          </div>

          <!-- Marca -->
          <div v-if="brands && brands.length">
            <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">Marca</label>
            <select 
              v-model="quickProductForm.brand_id"
              class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-white"
            >
              <option value="">Genérico / Por defecto</option>
              <option v-for="b in brands" :key="b.id" :value="b.id">{{ b.name }}</option>
            </select>
          </div>

          <div class="flex justify-end items-center gap-3 pt-4 border-t border-gray-100">
            <button 
              type="button" 
              class="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
              @click="showQuickProductModal = false"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              :disabled="isSavingProduct"
              class="px-5 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm"
            >
              <i v-if="isSavingProduct" class="fa fa-spinner fa-spin" />
              <i v-else class="fa fa-check" />
              <span>Guardar y Agregar a Compra</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import ShoppingPaymentBox from '@/Shared/ShoppingPaymentBox'
import pickBy from 'lodash/pickBy'
import debounce from 'lodash/debounce'
import Swal from 'sweetalert2'
import axios from 'axios'

export default {
  name: 'ShoppingCreate',

  components: {
    Head,
    Link,
    TextInput,
    SelectInput,
    ShoppingPaymentBox,
  },
  
  layout: Layout,
  
  props: {
    products: Array,
    deposits: Array,
    providers: Array,
    payment_methods: Array,
    current_exchange_rate: Number,
    brands: Array,
    categories: Array,
    spent_categories: Array,
    initial_type: {
      type: String,
      default: 'shopping',
    },
  },

  data() {
    const urlType = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('type') : null
    const defaultType = urlType === 'spent' || this.initial_type === 'spent' ? 'spent' : 'shopping'

    return {
      entryType: defaultType,
      isSavingSpent: false,
      spentBsAmount: null,
      spentForm: {
        name: '',
        spent_category_id: (this.spent_categories && this.spent_categories.length) ? this.spent_categories[0].id : '',
        provider_id: null,
        date: new Date().toISOString().slice(0, 10),
        method: 'efectivo',
        currency: 'USD',
        amount: null,
        exchange_rate: parseFloat(this.current_exchange_rate) || 0,
        description: '',
      },
      currency: 'USD', // 'USD' o 'VES'
      exchange_rate: parseFloat(this.current_exchange_rate) || 0,
      providersList: [...(this.providers || [])],
      discount_amount_bs: '',

      // Modal Crear Proveedor
      showQuickProviderModal: false,
      isSavingProvider: false,
      providerErrors: {},
      quickProviderForm: {
        name: '',
        company_name: '',
        tax_id: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        notes: '',
      },

      // Modal Crear Producto
      showQuickProductModal: false,
      isSavingProduct: false,
      productErrors: {},
      quickProductForm: {
        name: '',
        code: '',
        product_category_id: '',
        brand_id: '',
        cost: '',
        cost_bs: '',
        price: '',
        price_bs: '',
      },

      form: this.$inertia.form({
        provider_id: '',
        date: new Date().toISOString().slice(0, 10),
        invoice_number: '',
        payment_type: 'cash',
        due_date: '',
        deposit_id: '',
        payment_method_id: '',
        discount_percentage: '',
        discount_amount: '',
        total: '',
        sub_total: '',
        payments: [],
        search: {
          terms: '',
        },
        products: [],
      }),
      selectedPaymentMethod: null,
    }
  },

  computed: {
    spentCategoriesList() {
      return this.spent_categories || []
    },
    exchangeRate() {
      return parseFloat(this.current_exchange_rate) || parseFloat(this.exchange_rate) || 0
    },
    sellingPrices() {
      return (product) => {
        const cost = parseFloat(product?.cost) || 0
        const percentage = parseFloat(product?.percentage) || 0
        const profit = (cost * percentage) / 100
        return cost + profit
      }
    },
  },

  methods: {
    back() {
      window.history.back()
    },

    setCurrency(curr) {
      this.currency = curr
      const rate = parseFloat(this.exchange_rate) || 0
      
      // Sincronizar costos en la lista de productos
      if (rate > 0) {
        this.form.products.forEach(p => {
          if (curr === 'VES') {
            p.cost_bs = parseFloat(((p.cost || 0) * rate).toFixed(2))
          } else {
            if (p.cost_bs) {
              p.cost = parseFloat((p.cost_bs / rate).toFixed(4))
            }
          }
        })

        // Sincronizar monto de descuento
        if (curr === 'VES') {
          if (this.form.discount_amount) {
            this.discount_amount_bs = (parseFloat(this.form.discount_amount) * rate).toFixed(2)
          } else {
            this.discount_amount_bs = ''
          }
        }
      }
    },

    onExchangeRateChange() {
      const rate = parseFloat(this.exchange_rate) || 0
      if (rate > 0) {
        this.form.products.forEach(p => {
          if (this.currency === 'USD') {
            p.cost_bs = parseFloat(((p.cost || 0) * rate).toFixed(2))
          } else {
            if (p.cost_bs) {
              p.cost = parseFloat((p.cost_bs / rate).toFixed(4))
            }
          }
        })

        if (this.currency === 'VES' && this.discount_amount_bs) {
          this.form.discount_amount = (parseFloat(this.discount_amount_bs) / rate).toFixed(2)
        } else if (this.form.discount_amount) {
          this.discount_amount_bs = (parseFloat(this.form.discount_amount) * rate).toFixed(2)
        }
      }
    },

    updateCostFromUSD(product) {
      const rate = parseFloat(this.exchange_rate) || 0
      if (rate > 0) {
        product.cost_bs = parseFloat(((product.cost || 0) * rate).toFixed(2))
      }
    },

    updateCostFromBs(product) {
      const rate = parseFloat(this.exchange_rate) || 0
      if (rate > 0) {
        product.cost = parseFloat(((product.cost_bs || 0) / rate).toFixed(4))
      }
    },

    getCostBs(product) {
      if (product.cost_bs !== undefined && product.cost_bs !== null && product.cost_bs !== '') {
        return parseFloat(product.cost_bs) || 0
      }
      const rate = parseFloat(this.exchange_rate) || 0
      return parseFloat(((product.cost || 0) * rate).toFixed(2))
    },

    getCostInBs(costUSD) {
      const rate = parseFloat(this.exchange_rate) || 0
      return (parseFloat(costUSD) || 0) * rate
    },

    onDiscountBsChange() {
      const rate = parseFloat(this.exchange_rate) || 0
      if (this.discount_amount_bs === '' || this.discount_amount_bs === null) {
        this.form.discount_amount = ''
        this.form.discount_percentage = ''
        return
      }

      if (rate > 0) {
        const amountUSD = (parseFloat(this.discount_amount_bs) || 0) / rate
        this.form.discount_amount = amountUSD.toFixed(2)
        this.calculateDiscountPercentage()
      }
    },

    openQuickProviderModal() {
      this.quickProviderForm = {
        name: '',
        company_name: '',
        tax_id: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        notes: '',
      }
      this.providerErrors = {}
      this.showQuickProviderModal = true
    },

    async saveQuickProvider() {
      this.isSavingProvider = true
      this.providerErrors = {}

      try {
        const response = await axios.post('/providers/quick-provider', this.quickProviderForm)
        if (response.data?.success && response.data?.provider) {
          const newProv = response.data.provider
          this.providersList.push(newProv)
          this.form.provider_id = newProv.id
          this.showQuickProviderModal = false
          Swal.fire({
            icon: 'success',
            title: 'Proveedor creado',
            text: `El proveedor "${newProv.name}" fue creado y seleccionado.`,
            timer: 2000,
            showConfirmButton: false,
          })
        }
      } catch (error) {
        if (error.response?.data?.errors) {
          this.providerErrors = error.response.data.errors
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error al crear proveedor',
            text: error.response?.data?.message || error.message,
          })
        }
      } finally {
        this.isSavingProvider = false
      }
    },

    openQuickProductModal() {
      this.quickProductForm = {
        name: this.form.search.terms || '',
        code: '',
        product_category_id: '',
        brand_id: '',
        cost: '',
        cost_bs: '',
        price: '',
        price_bs: '',
      }
      this.productErrors = {}
      this.showQuickProductModal = true
    },

    async saveQuickProduct() {
      this.isSavingProduct = true
      this.productErrors = {}

      const rate = parseFloat(this.exchange_rate) || 0
      let costUSD = this.quickProductForm.cost
      let priceUSD = this.quickProductForm.price

      if (this.currency === 'VES' && rate > 0) {
        if (this.quickProductForm.cost_bs) {
          costUSD = parseFloat((this.quickProductForm.cost_bs / rate).toFixed(4))
        }
        if (this.quickProductForm.price_bs) {
          priceUSD = parseFloat((this.quickProductForm.price_bs / rate).toFixed(2))
        }
      }

      const payload = {
        name: this.quickProductForm.name,
        code: this.quickProductForm.code,
        product_category_id: this.quickProductForm.product_category_id,
        brand_id: this.quickProductForm.brand_id,
        cost: costUSD,
        price: priceUSD,
      }

      try {
        const response = await axios.post('/inventories/quick-product', payload)
        if (response.data?.success && response.data?.product) {
          const newProd = response.data.product
          this.selectProduct(newProd)
          this.showQuickProductModal = false
          this.form.search.terms = ''
          Swal.fire({
            icon: 'success',
            title: 'Producto creado',
            text: `"${newProd.name}" creado y agregado a la compra.`,
            timer: 2000,
            showConfirmButton: false,
          })
        }
      } catch (error) {
        if (error.response?.data?.errors) {
          this.productErrors = error.response.data.errors
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error al crear producto',
            text: error.response?.data?.message || error.message,
          })
        }
      } finally {
        this.isSavingProduct = false
      }
    },

    addPayment(payment) {
      const total = this.getTotal()
      const currentTotalPaid = this.getTotalPaid()
      const newAmount = parseFloat(payment.amount) || 0
      const totalPaid = currentTotalPaid + newAmount

      if (this.form.payment_type === 'cash') {
        if (newAmount < (total - currentTotalPaid) - 0.01) {
          Swal.fire({
            icon: 'error',
            title: 'Pago incompleto',
            text: 'El pago de contado debe ser completo',
            confirmButtonColor: '#0d9488',
          })
          return
        }
        if (newAmount > (total - currentTotalPaid) + 0.01) {
          Swal.fire({
            icon: 'error',
            title: 'Monto excedido',
            text: 'El pago no debe exceder el monto a pagar',
            confirmButtonColor: '#0d9488',
          })
          return
        }
      } else {
        if (totalPaid > total + 0.01) {
          Swal.fire({
            icon: 'warning',
            title: 'Monto excedido',
            text: 'El monto del pago excede el total de la compra',
            confirmButtonColor: '#0d9488',
          })
          return
        }
      }

      const newPayment = {
        method: payment.method,
        amount: parseFloat(payment.amount) || 0,
        amount_bs: parseFloat(payment.amount_bs) || 0,
        exchange_rate: parseFloat(payment.exchange_rate) || parseFloat(this.exchange_rate) || null,
        reference: payment.reference || '',
        date: this.form.date,
        status: this.form.payment_type === 'cash' ? 'paid' : 'pending',
      }
      
      this.form.payments.push(newPayment)
      
      if (this.$refs.paymentBox) {
        this.$refs.paymentBox.resetForm()
      }
    },

    removePayment(index) {
      this.form.payments.splice(index, 1)
    },

    calculateDiscount() {
      if (this.form.discount_percentage === '' || this.form.discount_percentage === null) {
        this.form.discount_amount = ''
        this.discount_amount_bs = ''
        return
      }

      const percentage = parseFloat(this.form.discount_percentage)
      if (percentage >= 0 && percentage <= 100) {
        const subtotal = this.getSubtotal()
        const amount = (subtotal * percentage) / 100
        this.form.discount_amount = amount.toFixed(2)
        const rate = parseFloat(this.exchange_rate) || 0
        this.discount_amount_bs = (amount * rate).toFixed(2)
      } else {
        this.form.discount_amount = ''
        this.discount_amount_bs = ''
      }
    },

    calculateDiscountPercentage() {
      if (this.form.discount_amount === '' || this.form.discount_amount === null) {
        this.form.discount_percentage = ''
        this.discount_amount_bs = ''
        return
      }

      const subtotal = this.getSubtotal()
      const rate = parseFloat(this.exchange_rate) || 0
      if (subtotal > 0) {
        const amount = parseFloat(this.form.discount_amount)
        if (amount <= subtotal) {
          const percentage = (amount / subtotal) * 100
          this.form.discount_percentage = percentage.toFixed(2)
          this.discount_amount_bs = (amount * rate).toFixed(2)
        } else {
          this.form.discount_amount = subtotal.toFixed(2)
          this.form.discount_percentage = '100'
          this.discount_amount_bs = (subtotal * rate).toFixed(2)
        }
      } else {
        this.form.discount_percentage = ''
        this.discount_amount_bs = ''
      }
    },

    searchProducts() {
      this.$inertia.get('/shoppings/create', 
        pickBy({ search: this.form.search }), 
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        },
      )
    },

    selectProduct(product) {
      const exists = this.form.products.some(p => p.id === product.id)
      if (!exists) {
        const costUSD = parseFloat(product.cost) || 0
        const rate = parseFloat(this.exchange_rate) || 0
        const costBs = rate > 0 ? parseFloat((costUSD * rate).toFixed(2)) : 0

        this.form.products.push({
          ...product,
          quantity: 1,
          cost: costUSD,
          cost_bs: costBs,
          price: parseFloat(product.price) || 0,
          percentage: parseFloat(product.percentage) || 0,
        })
      }
    },

    removeProduct(product) {
      this.form.products = this.form.products.filter(p => p.id !== product.id)
    },

    getSubtotal() {
      return this.form.products ? this.form.products.reduce((sum, product) => sum + ((parseFloat(product.quantity) || 0) * (parseFloat(product.cost) || 0)), 0) : 0
    },

    getSubtotalBs() {
      const rate = parseFloat(this.exchange_rate) || 0
      return this.getSubtotal() * rate
    },

    getDiscountBs() {
      const rate = parseFloat(this.exchange_rate) || 0
      return (parseFloat(this.form.discount_amount) || 0) * rate
    },

    getTotal() {
      const subtotal = parseFloat(this.getSubtotal()) || 0
      const discount = this.form.discount_amount === '' ? 0 : parseFloat(this.form.discount_amount) || 0
      const total = subtotal - Math.min(discount, subtotal)
      return !isNaN(total) && total >= 0 ? total : 0
    },

    getTotalBs() {
      const rate = parseFloat(this.exchange_rate) || 0
      return this.getTotal() * rate
    },

    getTotalPaid() {
      return this.form.payments ? this.form.payments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0) : 0
    },

    getTotalPaidBs() {
      const rate = parseFloat(this.exchange_rate) || 0
      return this.getTotalPaid() * rate
    },

    formatUSD(value) {
      const val = parseFloat(value) || 0
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(val)
    },

    formatBs(value) {
      const val = parseFloat(value) || 0
      return 'Bs. ' + new Intl.NumberFormat('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(val)
    },

    formatCurrency(value, currency = 'USD') {
      if (currency === 'USD') return this.formatUSD(value)
      return this.formatBs(value)
    },

    generate() {
      if (!this.form.provider_id) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Debe seleccionar un proveedor', confirmButtonColor: '#0d9488' })
        return
      }
      if (!this.form.invoice_number) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Debe ingresar el número de factura', confirmButtonColor: '#0d9488' })
        return
      }
      if (!this.form.products.length) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Debe agregar al menos un producto', confirmButtonColor: '#0d9488' })
        return
      }
      
      const total = this.getTotal()
      if (total <= 0) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'El total de la compra debe ser mayor a cero', confirmButtonColor: '#0d9488' })
        return
      }

      if (this.form.payment_type === 'cash') {
        const totalPaid = this.getTotalPaid()
        if (Math.abs(totalPaid - total) > 0.01) {
          Swal.fire({ icon: 'error', title: 'Error', text: 'El pago de contado debe ser completo', confirmButtonColor: '#0d9488' })
          return
        }
      }
      
      if (!this.form.deposit_id) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Debe seleccionar un depósito', confirmButtonColor: '#0d9488' })
        return
      }

      // Preparar costos y totales finales en USD
      this.form.total = parseFloat(total).toFixed(2)
      this.form.sub_total = parseFloat(this.getSubtotal()).toFixed(2)
      this.form.status = this.form.payment_type === 'cash' ? 'paid' : 'pending'
      
      if (this.form.payment_type === 'cash' && !this.form.due_date) {
        this.form.due_date = this.form.date
      }

      // Asegurar que cada producto tenga cost y price en USD
      this.form.products.forEach(p => {
        p.cost = parseFloat(p.cost) || 0
        p.price = parseFloat(p.price) || (p.cost ? p.cost * 1.3 : 0)
        p.percentage = parseFloat(p.percentage) || 0
      })
      
      this.form.post('/shoppings')
    },

    getPaymentMethodName(methodId) {
      const method = (this.payment_methods || []).find(m => m.id === methodId)
      return method ? method.name : 'Desconocido'
    },

    setSpentCurrency(curr) {
      this.spentForm.currency = curr
      if (curr === 'USD') {
        this.spentBsAmount = null
      } else {
        this.spentForm.exchange_rate = this.exchangeRate
        this.calculateSpentAmountFromBs()
      }
    },

    calculateSpentAmountFromBs() {
      const rate = parseFloat(this.spentForm.exchange_rate) || this.exchangeRate
      if (this.spentBsAmount && rate > 0) {
        this.spentForm.amount = parseFloat((this.spentBsAmount / rate).toFixed(2))
      } else {
        this.spentForm.amount = 0
      }
    },

    saveSpent() {
      if (!this.spentForm.name) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Debe ingresar el concepto del gasto', confirmButtonColor: '#f59e0b' })
        return
      }
      if (!this.spentForm.amount || parseFloat(this.spentForm.amount) <= 0) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'El monto del gasto debe ser mayor a 0', confirmButtonColor: '#f59e0b' })
        return
      }
      if (!this.spentForm.spent_category_id) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Debe seleccionar una categoría de gasto', confirmButtonColor: '#f59e0b' })
        return
      }

      this.isSavingSpent = true
      this.$inertia.post('/shoppings', {
        transaction_type: 'spent',
        ...this.spentForm,
      }, {
        onSuccess: () => {
          this.isSavingSpent = false
          Swal.fire({
            icon: 'success',
            title: '¡Gasto Registrado!',
            text: 'El gasto ha sido guardado exitosamente como egreso al negocio.',
            timer: 2000,
            showConfirmButton: false,
          })
        },
        onError: (errors) => {
          this.isSavingSpent = false
          const msg = Object.values(errors)[0] || 'Error al guardar el gasto'
          Swal.fire({ icon: 'error', title: 'Error', text: msg, confirmButtonColor: '#f59e0b' })
        },
      })
    },
  },

  watch: {
    'form.search': {
      handler: debounce(function() {
        this.searchProducts()
      }, 300),
      deep: true,
    },
    'form.products': {
      handler() {
        if (parseFloat(this.form.discount_percentage) > 0) {
          this.calculateDiscount()
        }
      },
      deep: true,
    },
  },
}
</script>